// src/oura/merchant/Tour.tsx
//
// "Show me around": nine stops that explain the arrangement (#4062 MR-FE-10).
//
// IT NEVER STARTS BY ITSELF. A tour that runs on arrival is a thing to
// dismiss before you can use the product, and the owners this is for arrive
// with a job in mind. It starts from "Show me around" on the welcome card and
// from "How this works" under their own name, so it is there when wanted and
// nowhere when not.
//
// A stop whose target is not on screen is SKIPPED rather than pointed at
// nothing: the welcome card goes once set-up is done, and the tiles are not
// on every width. The last stop has no target and sits in the middle.
//
// Targets are found by `data-tour` and `data-nav-view`, which the rail and
// the phone bar both carry, so one stop points at whichever is showing.
//
// The menu stop lights the WHOLE menu, every item at once, and says how many
// pages there are by counting them, so the sentence stays true as pages come
// and go (Setup leaves with onboarding; Website arrives with #4210).

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { FOCUS_RING, PillButton, TYPE } from "@oura/ui";

const START_EVENT = "oura:tour:start";

/** Start the tour from anywhere: the welcome card, the avatar menu. */
export function startMerchantTour() {
    window.dispatchEvent(new CustomEvent(START_EVENT));
}

interface Stop {
    /** A CSS selector; null for a stop that belongs to no one thing. */
    target: string | null;
    title: string;
    text: string;
    /** The second paragraph, when there is something worth adding. */
    also?: string;
    /** Light every visible match together (the menu), not just the first. */
    spread?: true;
    /** Open the note beside the target, for a tall one it would cover from below. */
    beside?: true;
}

/** The stops, in the ticket's order. `{manager}` and `{pages}` are filled at render. */
const STOPS: Stop[] = [
    {
        target: "[data-nav-view]",
        spread: true,
        beside: true,
        title: "Your menu",
        text: "{pages} pages. Most weeks you only need two of them: Home and To do.",
    },
    {
        target: '[data-tour="welcome"]',
        title: "Start here",
        text: "Three things get you going: give us access, answer a few questions, and then we do the work.",
        also: "It stays on Home, folded, until they are done. Press it to see the steps.",
    },
    {
        target: '#tour-tile-ai',
        title: "How AI names you",
        text: "We ask AI assistants the questions your customers would ask, and count how many answers mention you.",
        also: "We check once a month. Open Visibility to see every question.",
    },
    {
        target: '#tour-tile-visits',
        title: "Visits from Google",
        text: "How many people clicked through to your website from a Google search.",
        also: "This fills in once Google is connected. Set-up is a tab in To do.",
    },
    {
        target: '[data-nav-view="questions"]',
        title: "Visibility",
        text: "How AI assistants name you, and who they name instead.",
        also: "The Website tab shows how easily AI can read your site.",
    },
    {
        target: '[data-nav-view="decisions"]',
        title: "To do",
        text: "When we need an answer or a photo from you, it appears here. The number shows how many are waiting.",
        also: "Your setup steps are here too, until they are done. We email you as well, so you do not have to keep checking.",
    },
    {
        target: "#this-month",
        title: "Our work",
        text: "Further down Home: every piece of work in your plan, and where it stands. Press a row to read what it is in plain words.",
        also: "Open a card to see what we fixed. Nothing needs you unless it says Needs you.",
    },
    {
        target: '[data-nav-view="reports"]',
        title: "Reports",
        text: "Each month we send a report on what changed and what we did. Read it here, or download it.",
    },
    {
        target: null,
        title: "Need a hand?",
        text: "{manager} is your account manager. Ask about any question from inside it, or ask for a change from Home.",
        also: "You can replay this tour from the menu under your name.",
    },
];

type Box = { top: number; left: number; width: number; height: number } | null;

/** Every visible match of `selector`: an element with no size is hidden or collapsed. */
function visibleRects(selector: string): DOMRect[] {
    return Array.from(document.querySelectorAll<HTMLElement>(selector))
        .map((el) => el.getBoundingClientRect())
        .filter((r) => r.width > 0 && r.height > 0);
}

const NUMBER_WORD = ["No", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten"];

/** The menu's page count, as the sentence's first word. */
function pagesWord(): string {
    const n = visibleRects("[data-nav-view]").length;
    return NUMBER_WORD[n] ?? String(n);
}

function boxOf(selector: string | null, spread?: boolean): Box {
    if (!selector) return null;
    if (spread) {
        // One box round every visible match: the rail's items on a laptop,
        // the bottom bar's on a phone. The rail's list itself runs on to the
        // footer, so lighting it would light empty space too.
        const rects = visibleRects(selector);
        if (rects.length === 0) return null;
        const top = Math.min(...rects.map((r) => r.top));
        const left = Math.min(...rects.map((r) => r.left));
        const bottom = Math.max(...rects.map((r) => r.bottom));
        const right = Math.max(...rects.map((r) => r.right));
        return { top, left, width: right - left, height: bottom - top };
    }
    // EVERY NAV SELECTOR MATCHES TWICE -- once in the rail, once in the phone
    // bar -- and only one of them is on screen at a given width. Taking the
    // first match meant that on a phone the hidden rail answered, with a
    // zero-sized box, and the five nav stops were filtered out as absent
    // while their visible targets sat right there (Jack, #4087).
    //
    // So: the first match that actually has a box. A zero-sized rect is how
    // both `display: none` and a collapsed element read, which is the same
    // question this is asking.
    const found = Array.from(document.querySelectorAll<HTMLElement>(selector))
        .map((el) => el.getBoundingClientRect())
        .find((r) => r.width > 0 && r.height > 0);
    return found ? { top: found.top, left: found.left, width: found.width, height: found.height } : null;
}

export function MerchantTour({ manager }: { manager?: string | null }) {
    const [at, setAt] = useState<number | null>(null);
    const [box, setBox] = useState<Box>(null);
    const noteRef = useRef<HTMLDivElement>(null);

    // Only the stops whose target is on screen, worked out when the tour
    // starts rather than per press: the page does not move underneath it, and
    // a list that changes mid-tour makes Back land somewhere else.
    const [stops, setStops] = useState<Stop[]>([]);

    const stop = at !== null ? stops[at] ?? null : null;

    useEffect(() => {
        const onStart = () => {
            setStops(STOPS.filter((s) => s.target === null || boxOf(s.target, s.spread)));
            setAt(0);
        };
        window.addEventListener(START_EVENT, onStart);
        return () => window.removeEventListener(START_EVENT, onStart);
    }, []);

    const end = useCallback(() => setAt(null), []);

    useLayoutEffect(() => {
        if (!stop) return;
        // A stop further down the page (Our work, on Home) is brought into
        // view first; the scroll listener below then moves the note with it.
        if (stop.target && !stop.spread) {
            const el = Array.from(document.querySelectorAll<HTMLElement>(stop.target)).find((e) => {
                const r = e.getBoundingClientRect();
                return r.width > 0 && r.height > 0;
            });
            const r = el?.getBoundingClientRect();
            if (el && r && (r.top < 0 || r.bottom > window.innerHeight)) el.scrollIntoView({ block: "center" });
        }
        setBox(boxOf(stop.target, stop.spread));
    }, [stop]);

    useEffect(() => {
        if (at === null) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") end();
        };
        const reposition = () => stop && setBox(boxOf(stop.target, stop.spread));
        window.addEventListener("keydown", onKey);
        window.addEventListener("resize", reposition);
        window.addEventListener("scroll", reposition, true);
        noteRef.current?.focus();
        return () => {
            window.removeEventListener("keydown", onKey);
            window.removeEventListener("resize", reposition);
            window.removeEventListener("scroll", reposition, true);
        };
    }, [at, stop, end]);

    if (at === null || !stop) return null;
    const last = at === stops.length - 1;
    const text = stop.text.replace("{manager}", manager?.trim() || "Your account manager").replace("{pages}", pagesWord());

    // Beside a tall target when it asks and there is room to its right (the
    // menu on a laptop), so the note does not cover what it explains. Else
    // under the target when there is room, otherwise above it. A stop with
    // no target sits in the middle of the screen.
    const besideFits = Boolean(box && stop.beside && box.left + box.width + 12 + 320 <= window.innerWidth - 12);
    const note = box && besideFits
        ? {
            top: Math.min(Math.max(12, box.top), Math.max(12, window.innerHeight - 232)),
            left: box.left + box.width + 12,
        }
        : box
        ? {
            top: box.top + box.height + 12 + 220 > window.innerHeight ? Math.max(12, box.top - 12 - 200) : box.top + box.height + 12,
            left: Math.min(Math.max(12, box.left), Math.max(12, window.innerWidth - 332)),
        }
        : { top: Math.max(12, window.innerHeight / 2 - 110), left: Math.max(12, window.innerWidth / 2 - 160) };

    return createPortal(
        <div role="dialog" aria-modal="true" aria-label={`${stop.title}. Stop ${at + 1} of ${stops.length}.`} className="fixed inset-0 z-[60]">
            {/* The dim is the way out as well: pressing anything that is not
                the note ends the tour, which is what people try first. With a
                target, the dim is the spotlight's own shadow, so the thing
                being explained stays at full brightness inside its ring
                instead of sitting under the dim with only the ring lit. */}
            <div aria-hidden className={cn("absolute inset-0", !box && "bg-gray-900/50")} onClick={end} />
            {box && (
                <div
                    aria-hidden
                    className="pointer-events-none absolute rounded-xl shadow-[0_0_0_9999px_rgba(17,24,39,0.5)] ring-2 ring-white ring-offset-2 ring-offset-blue-500"
                    style={{ top: box.top - 4, left: box.left - 4, width: box.width + 8, height: box.height + 8 }}
                />
            )}
            <div
                ref={noteRef}
                tabIndex={-1}
                style={{ top: note.top, left: note.left }}
                className={cn("absolute w-[320px] max-w-[calc(100vw-24px)] rounded-2xl bg-white p-4 shadow-2xl", FOCUS_RING)}
            >
                <p className={cn(TYPE.tt, "m-0 text-gray-900")}>{stop.title}</p>
                <p className={cn(TYPE.tb, "m-0 mt-1 text-gray-700")}>{text}</p>
                {stop.also && <p className={cn(TYPE.tc, "m-0 mt-2 text-gray-500")}>{stop.also}</p>}
                <div className="mt-3 flex items-center justify-between gap-3">
                    <div aria-hidden className="flex gap-1">
                        {stops.map((s, i) => (
                            <span key={s.title} className={cn("h-1.5 w-1.5 rounded-full", i === at ? "bg-blue-600" : "bg-gray-300")} />
                        ))}
                    </div>
                    {/* One size and one type for all three. Secondary on the
                        left, the primary always last, on the right: Next, or
                        Done on the last stop. */}
                    <div className="flex items-center gap-2">
                        {!last && (
                            <PillButton size="sm" onClick={end}>
                                Skip
                            </PillButton>
                        )}
                        {at > 0 && <PillButton size="sm" icon={ArrowLeftIcon} aria-label="Back" className="w-7 !px-0" onClick={() => setAt(at - 1)} />}
                        {last ? (
                            <PillButton size="sm" variant="primary" onClick={end}>
                                Done
                            </PillButton>
                        ) : (
                            <PillButton size="sm" variant="primary" icon={ArrowRightIcon} aria-label="Next" className="w-7 !px-0" onClick={() => setAt(at + 1)} />
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
}
